import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Loader2, FileDown, ShieldCheck, AlertCircle } from "lucide-react";
import { formatRupiah, formatTanggal } from "../lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicInvoicePage() {
    const { token } = useParams();
    const [inv, setInv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/public/invoice/${token}`);
                setInv(res.data);
            } catch (e) {
                setError(e?.response?.data?.detail || "Link tidak valid atau sudah dibatalkan");
            } finally { setLoading(false); }
        })();
    }, [token]);

    const downloadPDF = () => {
        window.open(`${API}/public/invoice/${token}/pdf`, "_blank");
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-teal-50/30 to-slate-50">
            <div className="text-center"><Loader2 className="w-8 h-8 text-teal-600 animate-spin mx-auto" /><div className="text-sm text-slate-500 mt-3">Memuat invoice...</div></div>
        </div>
    );

    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-teal-50/30 to-slate-50 p-6">
            <div className="max-w-md glass-card rounded-2xl p-8 text-center">
                <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-4"><AlertCircle className="w-6 h-6" /></div>
                <div className="text-lg font-bold text-slate-900 mb-1">Link Tidak Valid</div>
                <div className="text-sm text-slate-600">{error}</div>
            </div>
        </div>
    );

    const totalPeng = inv.total_bon_kain + inv.total_bon_peralatan + inv.total_kasbon + inv.total_potongan_reject;
    const totalPem = inv.total_setor + inv.total_kredit_reject_selesai + inv.saldo_awal;
    const tp = Number(inv.total_penghasilan_vendor);
    const pengaturan = inv.pengaturan_snapshot || {};

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-slate-50 py-8 px-4">
            <div className="max-w-3xl mx-auto space-y-5">
                <div className="flex items-center justify-between gap-3 flex-wrap px-1">
                    <div className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="w-4 h-4 text-teal-600" /> Halaman invoice publik · read-only</div>
                    <Button onClick={downloadPDF} data-testid="public-invoice-pdf-btn" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl gap-2"><FileDown className="w-4 h-4" /> Download PDF</Button>
                </div>

                <div data-testid="public-invoice-paper" className="glass-card rounded-2xl p-6 sm:p-10 space-y-6 shadow-lg">
                    <div className="pb-4 border-b border-slate-200">
                        <div className="text-xl font-extrabold text-teal-700">{pengaturan.nama_perusahaan || "CV. MERANGGI JAYA GARMENT"}</div>
                        {pengaturan.alamat && <div className="text-xs text-slate-600">{pengaturan.alamat}</div>}
                        <div className="text-xs text-slate-600">{[pengaturan.no_telp, pengaturan.email].filter(Boolean).join(" · ")}</div>
                    </div>

                    <div>
                        <div className="text-sm font-bold text-slate-900 tracking-wide mb-3">INVOICE VENDOR · {inv.nomor_invoice}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                            <div className="flex gap-3"><span className="text-slate-500 w-20 flex-shrink-0">Vendor</span><span className="font-semibold text-slate-900">{inv.nama_vendor_snapshot} ({inv.kode_vendor_snapshot})</span></div>
                            <div className="flex gap-3"><span className="text-slate-500 w-24 flex-shrink-0">Tanggal Tutup</span><span className="font-semibold text-slate-900">{formatTanggal(inv.tanggal_tutup)}</span></div>
                            <div className="flex gap-3"><span className="text-slate-500 w-20 flex-shrink-0">No HP</span><span className="text-slate-800">{inv.no_hp_snapshot || "-"}</span></div>
                            <div className="flex gap-3"><span className="text-slate-500 w-24 flex-shrink-0">Periode</span><span className="text-slate-800">{formatTanggal(inv.tanggal_mulai)} — {formatTanggal(inv.tanggal_akhir)}</span></div>
                            <div className="flex gap-3"><span className="text-slate-500 w-20 flex-shrink-0">Rekening</span><span className="text-slate-800">{inv.nama_bank_snapshot} {inv.no_rekening_snapshot}</span></div>
                        </div>
                    </div>

                    <div>
                        <div className="bg-teal-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-t-lg">Pengeluaran Vendor</div>
                        <div className="border border-t-0 border-slate-200 rounded-b-lg">
                            {[["Bon Kain", inv.total_bon_kain], ["Bon Peralatan", inv.total_bon_peralatan], ["Kasbon", inv.total_kasbon], ["Potongan Reject", inv.total_potongan_reject]].map(([l, v]) => (
                                <div key={l} className="flex justify-between items-center px-3 py-2 text-sm border-b border-slate-100 last:border-b-0"><span className="text-slate-700">{l}</span><span className="font-semibold num-tabular">{formatRupiah(v)}</span></div>
                            ))}
                            <div className="flex justify-between items-center px-3 py-2.5 bg-slate-50 rounded-b-lg"><span className="text-sm font-bold text-slate-900">SUBTOTAL PENGELUARAN</span><span className="font-bold num-tabular text-rose-700">{formatRupiah(totalPeng)}</span></div>
                        </div>
                    </div>

                    <div>
                        <div className="bg-teal-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-t-lg">Pemasukan Vendor</div>
                        <div className="border border-t-0 border-slate-200 rounded-b-lg">
                            {[["Setor Barang Jadi", inv.total_setor], ["Kredit Reject Selesai Diperbaiki", inv.total_kredit_reject_selesai], ["Saldo Awal", inv.saldo_awal]].map(([l, v]) => (
                                <div key={l} className="flex justify-between items-center px-3 py-2 text-sm border-b border-slate-100 last:border-b-0"><span className="text-slate-700">{l}</span><span className={`font-semibold num-tabular ${v < 0 ? "text-rose-700" : ""}`}>{formatRupiah(v)}</span></div>
                            ))}
                            <div className="flex justify-between items-center px-3 py-2.5 bg-slate-50 rounded-b-lg"><span className="text-sm font-bold text-slate-900">SUBTOTAL PEMASUKAN</span><span className="font-bold num-tabular text-emerald-700">{formatRupiah(totalPem)}</span></div>
                        </div>
                    </div>

                    <div className={`p-5 rounded-xl ${tp >= 0 ? "bg-emerald-50 border-2 border-emerald-300" : "bg-rose-50 border-2 border-rose-300"}`}>
                        <div className="flex justify-between items-center"><span className="text-base font-bold uppercase tracking-wider">Penghasilan Vendor</span><span className={`text-3xl font-extrabold num-tabular ${tp >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatRupiah(tp)}</span></div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8">
                        {["Dibuat oleh", "Dicek oleh", "Disetujui oleh"].map((label, idx) => {
                            const nama = [inv.dibuat_oleh, inv.dicek_oleh, inv.disetujui_oleh][idx];
                            return <div key={label} className="text-center"><div className="text-xs text-slate-500 mb-8">{label}</div><div className="border-t border-slate-400 pt-2 text-sm font-semibold text-slate-900">{nama}</div></div>;
                        })}
                    </div>
                </div>
                <div className="text-center text-xs text-slate-400">© {new Date().getFullYear()} {pengaturan.nama_perusahaan || "CV. MERANGGI JAYA GARMENT"}</div>
            </div>
        </div>
    );
}
